import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCompilation, getCompilationCover, getCompilationTracks, postCompilationTrack } from "../../components/apis/music-api";
import { Compilation as CompilationType } from '../../types/Compilation';
import { FileResponse } from "../../types/FileResponse";
import { Box, Button, Modal, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import { useMusicStore } from "../../components/store/store";
import { LastFmPlaylistItem } from "../../types/LastFmPlaylist";
import { format, getLetter, trackDuration, zeroPad } from "../../components/text-helper/text-helper";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { BeatLoader } from 'react-spinners';
import ButtonBar from "../../components/button-bar/ButtonBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCancel, faEdit, faPenToSquare, faPlay, faSave } from "@fortawesome/free-solid-svg-icons";
import { CompilationTrack } from "../../types/CompilationTrack";
import 'react-h5-audio-player/lib/styles.css';
import { postCompilationTrackRequest } from "../../types/messages/postCompilationTrackRequest";
import callApi from "../../components/call-api/call-api";
import { PlayModes } from "../../types/PlayModes";

type CompilationProps = {
    directory: string,
    albumName: string,
}

const Compilation = () => {
  const params = useParams<CompilationProps>();
  const { getMusicToken, getAccessToken } = useContext(AuthenticationContext);
  const [ compilation, setCompilation ] = useState<CompilationType>({ directory: '', year: 0, title: '', thumbnail: new Uint8Array(0), totalDuration: 0, trackCount: 0 });
  const [ cover, setCover ] = useState<FileResponse>();
  const [ tracks, setTracks ] = useState<CompilationTrack[]>();
  const [ open, setOpen ] = useState(false);
  const [ trackToEdit, setTrackToEdit ] = useState<CompilationTrack>();
  const [ trackToEditIndex, setTrackToEditIndex ] = useState(0);
  const [ totalTime, setTotalTime ] = useState(0);
  const { currentTrack, setPlaylist, setPlayMode } = useMusicStore();
  const isSmall = useMediaQuery('(max-width:900px)');
  const isLessThan500 = useMediaQuery('(max-width:580px)');
  const apiUrl = process.env.REACT_APP_API_URL;
  
  useEffect(() => {
    callApi<CompilationType>(getCompilation(getAccessToken(), params.directory ?? '', params.albumName ?? ''), setCompilation);
  }, [params, getAccessToken]);

  useEffect(() => {
    if (compilation !== undefined) {
      callApi<FileResponse>(getCompilationCover(getAccessToken(), params.directory ?? '', params.albumName ?? ''), setCover);
      callApi<CompilationTrack[]>(getCompilationTracks(getAccessToken(), params.directory ?? '', params.albumName ?? ''), setTracks);
    }
  }, [compilation, params.directory, params.albumName, getAccessToken]);
  
  useEffect(() => {
    if (tracks !== undefined) {
      const sum = tracks.reduce((accumulator, track) => accumulator + (track.duration ?? 0), 0);
      setTotalTime(sum);
    }
  }, [tracks]);

  const handlePlayClicked = (index?: number) => {
    if (tracks !== undefined) {
      const musicToken = getMusicToken();
      const selectedTracks = index === undefined ? tracks : [tracks[index - 1]];
      const trackList = selectedTracks.map((t) => {
        const trackName = `${zeroPad(t.number, 2)} - [ ${t.artistName} ] ${t.name}`;
        return {
          letter: "Compilations",
          artist: compilation.directory,
          album: params.albumName,
          trackNumber: t.number,
          name: t.name,
          scrobbleArtist: format(t.artistName ?? ''),
          src: `${apiUrl}/Stream/Compilations/${params.directory}/Albums/${(params.albumName ?? '')}/Tracks/${trackName}?accessToken=${musicToken}`
        } as LastFmPlaylistItem
      });

      setPlayMode(trackList.length > 1 ? PlayModes.Compilation : PlayModes.CompilationTrack);
      setPlaylist(trackList);
    }
  }
  
  const handleEditTrackClicked = (index?: number) => {    
    if (tracks !== undefined && index !== undefined) {
      setTrackToEditIndex(index);
      setTrackToEdit(tracks[index]);
      setOpen(true);
    }
  }

  const handleTrackUpdate = () => {
    if (tracks !== undefined && trackToEdit?.name !== undefined && trackToEdit.name.length > 0) {
      
      const updateTrack = async () => {
        try
        {
          const trackName = `${String(tracks[trackToEditIndex].number).padStart(2, '0')} - [ ${tracks[trackToEditIndex].artistName} ] ${tracks[trackToEditIndex].name}`;
          const trackRequest: postCompilationTrackRequest = {
            number: trackToEdit?.number,
            name: trackToEdit.name,
            duration: trackToEdit.duration ?? 0,
            artistName: trackToEdit.artistName ?? '',
          }

          const r = await postCompilationTrack(getAccessToken(), params.directory ?? '', params.albumName ?? '', trackName, trackRequest);
          if (r !== undefined) {
            callApi<CompilationTrack[]>(getCompilationTracks(getAccessToken(), params.directory ?? '', params.albumName ?? ''), setTracks);
          }

          setOpen(false);
        }
        catch
        {
  
        }
      }

      updateTrack();
    }
  }

  const isPlaying = (index: number) => {
    return currentTrack?.artist === params.directory
      && currentTrack?.album === params.albumName
      && currentTrack?.trackNumber === index;
  }

  const style = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      border: '1px solid #000',
      borderRadius: '12px',
      boxShadow: 24,
      p: 4,
  };
  
  return (
    <>
      <Modal open={open}>
        <Box sx={style}>
          <Stack spacing={1} direction="column">
            <Box>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Edit track number {String(trackToEdit?.number).padStart(2, '0')}
              </Typography>
            </Box>
            <Box>
              <TextField 
                id="track-artist"
                sx={{ width: '320px' }}
                size="small"
                label="Artist"
                InputLabelProps={{ shrink: true }}
                variant="outlined" 
                value={trackToEdit?.artistName}
                onChange={(e) => {
                  if (trackToEdit?.number) {
                    const t = {
                      ...trackToEdit,
                      artistName: e.target.value
                    };

                    setTrackToEdit(t);
                  }
                }}
              />
            </Box>
            <Box>
              <TextField 
                id="track-name"
                sx={{ width: '320px' }}
                size="small"
                label="Name"
                InputLabelProps={{ shrink: true }}
                variant="outlined" 
                value={trackToEdit?.name}
                onChange={(e) => {
                  if (trackToEdit?.number) {
                    const t = {
                      ...trackToEdit,
                      name: e.target.value
                    };

                    setTrackToEdit(t);
                  }
                }}
              />
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Button variant="contained" size="small" onClick={() => setOpen(false)} color="error">
                <FontAwesomeIcon fontSize="large" color="primary" icon={faCancel} className="icon" />
                Cancel
              </Button>
              <Button variant="contained" size="small" onClick={handleTrackUpdate} color="primary">
                <FontAwesomeIcon fontSize="large" color="primary" icon={faSave} className="icon" />
                Save
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>
      <ButtonBar
        leftButtons={[
          <Button variant="contained" size="small" onClick={() => handlePlayClicked()}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPlay} className="icon"/>
            Play
          </Button>
        ]}
        rightButtons={[          
          <Button component={Link} variant="contained" size="small" to={`/compilations/${params.directory}/albums/${(params.albumName ?? '')}/edit`}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPenToSquare} className="icon"/>
            Edit Compilation
          </Button>
        ]}
      />
      <Stack spacing={3}>
        <Box sx={{
          padding: '16px'
        }}>
          <Stack direction={isSmall ? 'column' : 'row'}>
            <Box sx={{ width: '100%', marginRight: '12px', paddingBottom: '24px' }}>
              <Stack direction={isSmall ? 'column' : 'row'} sx={{ paddingBottom: '12px' }}>
                <Box>
                  <Typography variant="h5">{compilation?.title}</Typography>
                  <Typography variant="body1">directory: <Link to={`/compilations/${params.directory}`}>{params.directory}</Link></Typography>
                </Box>
                <Box alignContent="right" sx={{ marginLeft: 'auto', textAlign: 'right' }}> 
                  <Typography variant="h5">{compilation?.year}</Typography>
                  <Typography variant="h6" color="grey">({trackDuration(totalTime)})</Typography>
                </Box>
              </Stack>
              <Box>
                <Stack spacing={1}>
                  {tracks?.map(track =>
                    <Stack
                      direction={isSmall ? 'column' : 'row'}
                      sx={{ ...(isSmall && { borderBottom: '1px solid #888' }), paddingBottom: '8px' }}
                      spacing={2}
                    >
                      <Box>
                        <Stack direction="row" spacing={0.5}>                    
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              style={{ minWidth: 0 }}
                              onClick={() => handleEditTrackClicked(track.number - 1)}>
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faEdit} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={isPlaying(track.number)}
                              style={{ minWidth: 0 }}
                              onClick={() => handlePlayClicked(track.number)}>
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faPlay} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        {String(track.number).padStart(2, '0')}
                      </Box>
                      <Box>
                        <Stack direction="column">
                          <Box pr={2}>
                            {track.name} ({trackDuration(track.duration)})
                          </Box>
                          <Box sx={{ fontSize: '0.8rem', color: 'grey' }}>
                            listens: {track.listenCount}
                          </Box>
                          <Box sx={{ color: '#999999' }}>
                            <Link to={`/letters/${getLetter(track.artistName ?? '')}/artists/${(track.artistName ?? '')}`}>{format(track.artistName ?? '')}</Link>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
            <Box
              alignContent={isSmall ? 'center' : 'right'}
              sx={{ textAlign: 'center' }}
            >
              <Box sx={{ width: isLessThan500 ? '100%' : '500px' }}>
                <Box
                  component="img"
                  alt={'Album Cover'}
                  sx={{ width: isLessThan500 ? '100%' : '500px', border: '1px solid #888' }}
                  src={`data:image/jpeg;base64,${cover?.fileContents}`}
                />
                <BeatLoader
                  loading={cover?.fileContents === undefined}
                  size={24}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              </Box>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </>
  )
}

export default Compilation;